import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  Check,
  Edit3,
  Loader2,
  RefreshCw,
  Sparkles,
  X,
} from "lucide-react";
import {
  reviewAnamnese,
  trackContextualAIEvent,
  updateContextualAnalysisStatus,
} from "../../services/contextualAiApi";


const STATUS_LABELS = {
  generated: "Sugestão da IA",
  accepted: "Revisado pela profissional",
  edited: "Editado pela profissional",
  dismissed: "Descartado",
  stale: "Análise desatualizada",
};


const normalizeAnalysis = (analysis) => {
  if (!analysis) return null;
  if (analysis.payload) return analysis;
  return {
    id: analysis.analysis_id,
    status: analysis.review_status || "generated",
    payload: analysis,
  };
};


export default function AnamneseAIReviewCard({
  patientId,
  responseId,
  existingAnalysis,
  onAnalysisChange,
  showToast,
  compact = false,
}) {
  const [analysis, setAnalysis] = useState(() => normalizeAnalysis(existingAnalysis));
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editedSummary, setEditedSummary] = useState("");
  const shownTracked = useRef(false);

  useEffect(() => {
    setAnalysis(normalizeAnalysis(existingAnalysis));
  }, [existingAnalysis]);

  useEffect(() => {
    if (shownTracked.current) return;
    shownTracked.current = true;
    trackContextualAIEvent("ai_context_action_shown", {
      action: "review",
      metadata: { context_type: "anamnese" },
    });
  }, []);

  const payload = analysis?.payload || {};
  const status = analysis?.status || "generated";
  const displayedSummary =
    status === "edited" && analysis?.edited_content
      ? analysis.edited_content
      : payload.summary || "";
  const canReview = Boolean(patientId && responseId);
  const requiresNewReview = !analysis || status === "stale" || status === "dismissed";

  const attentionItems = useMemo(
    () => (Array.isArray(payload.risk_flags) ? payload.risk_flags : []),
    [payload.risk_flags]
  );
  const questions = useMemo(
    () =>
      Array.isArray(payload.followup_questions)
        ? payload.followup_questions.slice(0, 6)
        : [],
    [payload.followup_questions]
  );

  const notify = (message, type = "success") => showToast?.(message, type);

  const publishAnalysis = (nextAnalysis) => {
    setAnalysis(nextAnalysis);
    onAnalysisChange?.(nextAnalysis);
  };

  const runReview = async () => {
    if (!canReview || loading) return;
    setLoading(true);
    const startedAt = Date.now();
    trackContextualAIEvent("ai_context_action_clicked", {
      action: "review",
      metadata: { context_type: "anamnese" },
    });
    try {
      const result = await reviewAnamnese({ patientId, responseId });
      const nextAnalysis = normalizeAnalysis(result);
      publishAnalysis(nextAnalysis);
      trackContextualAIEvent("ai_context_generation_success", {
        action: "review",
        outcome: result.generation_mode || "rules_only",
        duration_ms: Date.now() - startedAt,
        analysis_id: result.analysis_id,
        metadata: {
          context_type: "anamnese",
          status: result.review_status || "generated",
        },
      });
      notify("Revisão da anamnese concluída.");
    } catch (error) {
      trackContextualAIEvent("ai_context_generation_failed", {
        action: "review",
        outcome: "error",
        duration_ms: Date.now() - startedAt,
        metadata: {
          context_type: "anamnese",
          error_code: String(error?.response?.status || "request_failed"),
        },
      });
      notify(
        error?.response?.data?.detail ||
          "Não foi possível revisar esta anamnese agora.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const changeStatus = async (nextStatus, content = null) => {
    if (!analysis?.id || loading) return;
    setLoading(true);
    try {
      const updated = await updateContextualAnalysisStatus(
        analysis.id,
        nextStatus,
        content
      );
      publishAnalysis(updated);
      setEditing(false);
      const eventByStatus = {
        accepted: "ai_context_result_accepted",
        edited: "ai_context_result_edited",
        dismissed: "ai_context_result_dismissed",
      };
      trackContextualAIEvent(eventByStatus[nextStatus], {
        action: "review",
        analysis_id: analysis.id,
        metadata: { context_type: "anamnese", status: nextStatus },
      });
      notify(
        nextStatus === "accepted"
          ? "Revisão confirmada pela profissional."
          : nextStatus === "edited"
          ? "Resumo revisado e salvo."
          : "Sugestão descartada."
      );
    } catch (error) {
      notify(
        error?.response?.data?.detail || "Não foi possível atualizar a revisão.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  if (!analysis || requiresNewReview) {
    return (
      <div className="rounded-xl border border-purple-200 bg-purple-50 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-purple-700" />
            <div>
              <p className="font-semibold text-gray-900">
                {status === "stale"
                  ? "A anamnese mudou desde a última revisão"
                  : status === "dismissed"
                  ? "Revisão com IA descartada"
                  : "Revise esta anamnese com IA"}
              </p>
              <p className="mt-1 text-sm text-gray-600">
                A IA organiza as respostas para sua conferência. Nada será alterado automaticamente.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={runReview}
            disabled={!canReview || loading}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {status === "stale" || status === "dismissed" ? "Revisar novamente" : "Revisar com IA"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-purple-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-purple-700" />
          <div>
            <p className="font-semibold text-gray-900">Revisão assistida da anamnese</p>
            <span className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${status === "accepted" || status === "edited" ? "bg-green-100 text-green-800" : "bg-purple-100 text-purple-800"}`}>
              {STATUS_LABELS[status] || "Sugestão da IA"}
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={runReview}
          disabled={loading}
          className="inline-flex items-center gap-1 text-xs font-medium text-purple-700 hover:text-purple-900 disabled:opacity-60"
          title="Gerar uma nova revisão"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Revisar novamente
        </button>
      </div>

      {payload.generation_mode === "rules_only" && (
        <div className="mt-3 flex gap-2 rounded-lg bg-blue-50 p-3 text-sm text-blue-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>A revisão estruturada foi concluída. O complemento generativo não estava disponível.</span>
        </div>
      )}

      {editing ? (
        <div className="mt-4">
          <label className="mb-1 block text-sm font-medium text-gray-700">
            Resumo revisado pela profissional
          </label>
          <textarea
            value={editedSummary}
            onChange={(event) => setEditedSummary(event.target.value)}
            rows={6}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200"
          />
          <div className="mt-2 flex justify-end gap-2">
            <button type="button" onClick={() => setEditing(false)} className="rounded-lg border px-3 py-1.5 text-sm hover:bg-gray-50">
              Cancelar
            </button>
            <button type="button" onClick={() => changeStatus("edited", editedSummary)} disabled={!editedSummary.trim() || loading} className="rounded-lg bg-purple-600 px-3 py-1.5 text-sm text-white hover:bg-purple-700 disabled:opacity-60">
              Salvar revisão
            </button>
          </div>
        </div>
      ) : (
        <>
          {displayedSummary && (
            <div className="mt-4">
              <p className="text-sm font-semibold text-gray-800">Resumo</p>
              <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-gray-700">{displayedSummary}</p>
            </div>
          )}

          {!compact && attentionItems.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-semibold text-gray-800">Pontos para confirmar</p>
              <ul className="mt-2 space-y-1.5 text-sm text-gray-700">
                {attentionItems.map((item, index) => <li key={index}>• {item}</li>)}
              </ul>
            </div>
          )}

          {!compact && questions.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-semibold text-gray-800">Perguntas complementares</p>
              <ul className="mt-2 space-y-1.5 text-sm text-gray-700">
                {questions.map((item, index) => <li key={index}>• {item}</li>)}
              </ul>
            </div>
          )}

          <p className="mt-4 text-xs text-gray-500">
            Apoio à revisão. Confirme as informações com o paciente e use seu julgamento clínico.
          </p>

          {status === "generated" && (
            <div className="mt-4 flex flex-wrap justify-end gap-2 border-t pt-3">
              <button type="button" onClick={() => changeStatus("dismissed")} disabled={loading} className="inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
                <X className="h-4 w-4" /> Descartar
              </button>
              <button type="button" onClick={() => { setEditedSummary(displayedSummary); setEditing(true); }} disabled={loading} className="inline-flex items-center gap-1 rounded-lg border border-purple-200 px-3 py-1.5 text-sm text-purple-700 hover:bg-purple-50">
                <Edit3 className="h-4 w-4" /> Editar resumo
              </button>
              <button type="button" onClick={() => changeStatus("accepted")} disabled={loading} className="inline-flex items-center gap-1 rounded-lg bg-green-600 px-3 py-1.5 text-sm text-white hover:bg-green-700 disabled:opacity-60">
                <Check className="h-4 w-4" /> Confirmar revisão
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
